import { z } from 'zod';

import { FormConstants } from '@/components/Forms/constants';
import {
  datePattern,
  INVALID_EMAIL_SYMBOLS,
  isEmailRegExp,
  isForbiddenEmail,
  isLatinLetterRegExp,
  isRussianLetterRegExp,
  UA_MOBILE_PHONE_CODES_BY_OPERATOR,
} from '@/components/Forms/Validation';

import { formatDate } from '@/helpers/dates';

import { DATE_STRING_SEPARATOR } from '@/lib/consts';

import { DocumentTypeCode } from '@/api/types';

export const zodStringRequired = z
  .string({ errorMap: () => ({ message: 'Необхідно заповнити поле' }) })
  .min(1, { message: 'Необхідно заповнити поле' });
export const zodStringRequiredWithMinMax = (
  options: {
    min?: number;
    max?: number;
    messages?: {
      min?: string;
      max?: string;
    };
  } = {},
) => {
  const { min, max = FormConstants.INPUT_MAX_LENGTH, messages } = options;

  let schema = zodStringRequired;

  if (typeof min === 'number') {
    schema = schema.min(min, messages?.min ?? `Мінімальна кількість символів - ${min}`);
  }
  if (typeof max === 'number') {
    schema = schema.max(max, messages?.max ?? `Допустима кількість символів - ${max}`);
  }

  return schema;
};

export const zodUkrainianName = () => {
  return zodStringRequiredWithMinMax({ max: 255 })
    .refine((value) => /^[А-Яа-яЇїІіЄєҐґ'`\-\s]+$/.test(value), 'Лише кирилиця, тире та апостроф')
    .refine((value) => !/[ЭЪЫЁэъыё]/.test(value), 'Не передбачено використання символів російського алфавіту');
};

export const zodStringOptional = z.union([zodStringRequired, z.literal('')]).nullish();
export const zodStringOptionalWithMinMax = (...props: Parameters<typeof zodStringRequiredWithMinMax>) =>
  zodStringOptional.or(zodStringRequiredWithMinMax(...props));

export const zodStringOnlyUkrainian = zodStringRequired
  .refine(
    (checkString) => !isRussianLetterRegExp.test(checkString),
    'Не передбачено використання символів російського алфавіту',
  )
  .refine(
    (checkString) => !isLatinLetterRegExp.test(checkString),
    'Не передбачено використання символів латинського алфавіту',
  );

export const zodStringOnlyUkrainianOptional = zodStringOptional
  .refine(
    (checkString) => (checkString ? !isRussianLetterRegExp.test(checkString) : true),
    'Не передбачено використання символів російського алфавіту',
  )
  .refine(
    (checkString) => (checkString ? !isLatinLetterRegExp.test(checkString) : true),
    'Не передбачено використання символів латинського алфавіту',
  );

export const zodArrayRequired = (arrayItemType: z.ZodTypeAny) =>
  z
    .array(arrayItemType, { errorMap: () => ({ message: 'Необхідно заповнити поле' }) })
    .min(1, 'Необхідно заповнити поле');
export const zodArrayRequiredWithMinMax = (
  arrayItemType: z.ZodTypeAny,
  options: {
    min?: number;
    max?: number;
    messages?: {
      min?: string;
      max?: string;
    };
  } = {},
) => {
  const { min = 1, max, messages } = options;

  let schema = zodArrayRequired(arrayItemType);

  if (typeof min === 'number') {
    schema = schema.min(min, messages?.min ?? `Мінімальна кількість елементів - ${min}`);
  }
  if (typeof max === 'number') {
    schema = schema.max(max, messages?.max ?? `Допустима кількість елементів - ${max}`);
  }

  return schema;
};

export const zodArrayOptional = (arrayItemType: z.ZodTypeAny) => z.array(arrayItemType).optional().default([]);
export const zodArrayOptionalWithMinMax = (props: Parameters<typeof zodArrayRequiredWithMinMax>) =>
  zodArrayOptional(props[0]).or(zodArrayRequiredWithMinMax(...props));

export const zodDateString = zodStringRequired.regex(
  new RegExp(datePattern(DATE_STRING_SEPARATOR)),
  'Невірний формат дати',
);

export const zodPhone = zodStringRequired.regex(/^\+\d{12}$/, 'Неправильно вказаний номер телефону');
export const zodPhoneMissingPerson = zodStringRequired.regex(/^\+\d{5,20}$/, 'Неправильно вказаний номер телефону');

export const zodMobilePhoneUA = zodPhone.refine(
  (phone) => Object.values(UA_MOBILE_PHONE_CODES_BY_OPERATOR).flat(1).includes(phone.slice(3, 6)),
  'Недопустимий формат телефону',
);

export const zodEmail = zodStringRequired
  .max(254, 'Введений Email перевищує максимальну дозволену кількість символів')
  .regex(isEmailRegExp, 'Email має бути в форматі example@example.com')
  .refine((email) => {
    const [localPart, domainPart] = email.toLowerCase().split('@');
    const isLocalPartLengthValid = localPart?.length <= 64;
    const isDomainPartLengthValid = domainPart?.split('.').every((domain) => domain.length <= 64);

    return isLocalPartLengthValid && isDomainPartLengthValid;
  }, 'Недопустимий Email')
  .refine((email) => email.split('').every((symbol) => !INVALID_EMAIL_SYMBOLS.includes(symbol)), 'Недопустимий Email')
  .refine((email) => !isForbiddenEmail(email), 'Недопустимий поштовий сервіс');

export const zodEmailOptional = z.union([zodEmail, z.literal('')]).nullish();

export const zodSelectOption = z.object(
  {
    id: zodStringRequired,
    label: zodStringRequired,
  },
  { message: 'Необхідно заповнити поле' },
);

export const zodSelectOptionWithCode = z.object(
  {
    id: zodStringRequired,
    label: zodStringRequired,
    code: z.nativeEnum(DocumentTypeCode),
  },
  { message: 'Необхідно заповнити поле' },
);

const attachmentObject = z.object({
  uuid: z.string().uuid(),
  file_name: z.string(),
  mime_type: z.string(),
  form_field: z.string(),
  document_type: z.nativeEnum(DocumentTypeCode).nullable(),
  link: z.string().nullable(),
  encoded_content: z.string().nullable(),
});

const savedAttachmentArray = z
  .array(attachmentObject, {
    required_error: 'Файл обовʼязковий до завантаження',
  })
  .min(1, 'Файл обовʼязковий до завантаження');

export const savedAttachment = z.preprocess((val) => {
  if (val === undefined || val === null) {
    return [];
  }
  return val;
}, savedAttachmentArray);

const documentAttachmentObject = z.object({
  id: z.string().optional(),
  uuid: z.string().uuid().optional(),
  typeDoc: z.string().optional(),
  fileName: z.string().optional(),
  file_name: z.string().optional(),
  mime_type: z.string().optional(),
  code: z.string().optional(),
  flag: z.string().optional(),
  info: z
    .preprocess((val) => {
      if (val === undefined || val === null) return {};
      if (Array.isArray(val)) return {};
      return val;
    }, z.record(z.any()))
    .optional(),
  addressType: z.string().nullable().optional(),
  formField: z.string(),
  files: z
    .union([
      z.array(z.string(), { required_error: 'Файл обовʼязковий до завантаження' }),
      z.array(
        z.object({
          id: z.string(),
          file_name: z.string().optional(),
          fileName: z.string().optional(),
        }),
        { required_error: 'Файл обовʼязковий до завантаження' },
      ),
    ])
    .refine((val) => Array.isArray(val) && val.length >= 1, {
      message: 'Файл обовʼязковий до завантаження',
    }),
  fileNames: z.record(z.string(), z.string()).optional(),
  base_file: z.array(z.any()).optional(),
});

export const savedDocumentAttachment = z.preprocess((val) => {
  if (val === undefined || val === null) {
    return {};
  }
  if (Array.isArray(val)) {
    return {};
  }
  return val;
}, documentAttachmentObject.strict());

export const zodPlaceholder = z.any(); // will be validated in superRefine

export const zodCheckbox = z.boolean().nullish();
export const zodCheckboxOnlyTrue = z.literal(true, { message: 'Необхідно підтвердити' });

export const zodDate = (options?: { min?: Date | (() => Date); max?: Date | (() => Date) }) =>
  zodDateString.superRefine((val, ctx) => {
    const [year, month, day] = val.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    if (isNaN(date.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message: 'Недійсна дата',
      });
      return;
    }

    const minDate = typeof options?.min === 'function' ? options.min() : options?.min;
    if (minDate && date < minDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: 'date',
        inclusive: true,
        minimum: minDate.getTime(),
        message: `Дата має бути після ${formatDate(minDate)}`,
      });
    }

    const maxDate = typeof options?.max === 'function' ? options.max() : options?.max;
    if (maxDate && date > maxDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        type: 'date',
        inclusive: true,
        maximum: maxDate.getTime(),
        message: `Дата має бути до ${formatDate(maxDate)}`,
      });
    }
  });

export const zodGenericCyrillicText = z.object({
  message: z
    .string()
    .max(500, 'Максимальна довжина 500 символів')
    .refine((value) => /^[А-ЩЬЮЯЇІЄҐа-щьюяїієґA-Za-z0-9,.\-'()"\s]*$/.test(value), 'Недопустимий формат')
    .refine((value) => !/[ЭЪЫЁэъыё]/.test(value), 'Не передбачено використання символів російського алфавіту'),
});

export const zodWeapon = z
  .object({
    weaponType: z.enum(['firearm', 'firearm_part', 'cold_steel']),
    id: zodStringRequired,
  })
  .passthrough();

export const zodGenderRequired = z
  .enum(['M', 'F', 'U'], { errorMap: () => ({ message: 'Необхідно заповнити поле' }) })
  .refine((val) => val !== 'U', { message: 'Необхідно заповнити поле' });

export const zodSettlementUkrainian = zodStringRequired
  .max(120, 'Максимальна довжина 120 символів')
  .refine((value) => /^[А-ЩЬЮЯЇІЄҐа-щьюяїієґ0-9\-'\s]*$/.test(value), 'Тільки українська мова, тире та цифри');
